import { useLayoutEffect } from "react";

import { createPortal } from "react-dom";

import {
  PORTAL_DROPDOWN_ATTRIBUTE,
  PORTAL_DROPDOWN_Z_INDEX_VAR,
  PortalDropdownContainer,
} from "./shared.styles";

const ABOVE_IMPERSONATION_PANEL_Z_INDEX = "10001";
const ANCHOR_GAP = 4;
const VIEWPORT_MARGIN = 8;

type DropdownPosition = {
  left: number;
  maxHeight: number;
  top: number;
  width: number;
};

type PortalDropdownProps = {
  anchorRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  dropdownRef: React.RefObject<HTMLDivElement>;
  fullScreen?: boolean;
  minWidth?: number;
  variant?: "field" | "menu";
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), Math.max(min, max));

const getPosition = (
  anchor: HTMLElement,
  dropdown: HTMLElement | null,
  minWidth: number,
  variant: "field" | "menu",
): DropdownPosition => {
  const anchorRect = anchor.getBoundingClientRect();
  const contentWidth = variant === "menu" ? (dropdown?.offsetWidth ?? 0) : 0;
  const width = Math.max(anchorRect.width, minWidth, contentWidth);
  const height = dropdown?.offsetHeight ?? 0;

  const spaceBelow = window.innerHeight - anchorRect.bottom - ANCHOR_GAP;
  const spaceAbove = anchorRect.top - ANCHOR_GAP;
  const flip = height + VIEWPORT_MARGIN > spaceBelow && spaceAbove > spaceBelow;
  const maxHeight = Math.max(
    (flip ? spaceAbove : spaceBelow) - VIEWPORT_MARGIN,
    0,
  );
  const top = flip
    ? anchorRect.top - ANCHOR_GAP - Math.min(height, maxHeight)
    : anchorRect.bottom + ANCHOR_GAP;

  return {
    left: clamp(
      variant === "menu" ? anchorRect.right - width : anchorRect.left,
      VIEWPORT_MARGIN,
      window.innerWidth - width - VIEWPORT_MARGIN,
    ),
    maxHeight,
    top,
    width,
  };
};

const PortalDropdown = ({
  anchorRef,
  children,
  dropdownRef,
  fullScreen,
  minWidth = 0,
  variant = "field",
}: PortalDropdownProps): React.ReactElement => {
  useLayoutEffect(() => {
    const dropdown = dropdownRef.current;
    if (!fullScreen || !dropdown) return undefined;

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    document.body.style.setProperty(
      PORTAL_DROPDOWN_Z_INDEX_VAR,
      ABOVE_IMPERSONATION_PANEL_Z_INDEX,
    );
    dropdown.style.inset = "0";
    dropdown.style.width = "100%";
    dropdown.style.height = "100dvh";
    dropdown.style.borderRadius = "0";
    dropdown.style.visibility = "visible";

    return () => {
      document.body.style.overflow = overflow;
      document.body.style.removeProperty(PORTAL_DROPDOWN_Z_INDEX_VAR);
    };
  }, [dropdownRef, fullScreen]);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const dropdown = dropdownRef.current;
    if (fullScreen || !anchor || !dropdown) return undefined;

    // Written straight to the node instead of through state: a scroll-driven
    // re-render lands a frame after the browser paints the scroll, which makes
    // the dropdown visibly lag behind its anchor.
    const update = (): void => {
      dropdown.style.maxHeight = "";
      if (variant === "menu") {
        dropdown.style.left = "";
        dropdown.style.width = "";
      }
      const { left, maxHeight, top, width } = getPosition(
        anchor,
        dropdown,
        minWidth,
        variant,
      );
      dropdown.style.maxHeight = `${maxHeight}px`;
      dropdown.style.left = `${left}px`;
      dropdown.style.top = `${top}px`;
      dropdown.style.width = `${width}px`;
      dropdown.style.visibility = "visible";
    };

    const handleScroll = (event: Event): void => {
      if (event.target instanceof Node && dropdown.contains(event.target)) {
        return;
      }
      update();
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(anchor);
    observer.observe(dropdown);
    window.addEventListener("scroll", handleScroll, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", update);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("resize", update);
    };
  }, [anchorRef, dropdownRef, fullScreen, minWidth, variant]);

  return createPortal(
    <PortalDropdownContainer
      {...{ [PORTAL_DROPDOWN_ATTRIBUTE]: "" }}
      ref={dropdownRef}
    >
      {children}
    </PortalDropdownContainer>,
    document.body,
  );
};

export { PortalDropdown, type PortalDropdownProps };
